import { useLocation, useFetcher, useNavigate } from "@remix-run/react";
import { IconMenu } from "@tabler/icons-react";
import {
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import { useStartTour } from "#app/components/tours/use-start-tour";
import { ORG_TEAM_SELECTION_CHANGED_EVENT } from "#app/contexts/org-team-context.tsx";
import { UserUIContext } from "#app/contexts/user-ui";
import { type loader as settingsDataLoader } from "#app/routes/resources+/settings-data";
import { type loader as userPlanLoader } from "#app/routes/resources+/user-plan";
import { useChatStore } from "#app/store/chat";
import { useSessionsStore } from "#app/store/sessions";
import { focusChatComposer } from "#app/utils/chat-focus.client";
import { getDesignPhaseBlockingState } from "#app/utils/design-phase-guard";
import { cn } from "#app/utils/misc.tsx";
import { resolveSidebarRecentVariant } from "#app/utils/sidebar-recent";
import { useOptionalUser } from "#app/utils/user";
import { ConfirmDialog } from "../new-ui/confirm-dialog";
import { SettingsModal } from "../settings-modal";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { MainLogo } from "./sidebar/main-logo";
import { MenuItems } from "./sidebar/menu-items";
import { ProfileDropdown, type ProfilePlan } from "./sidebar/profile-dropdown";
import { RecentChats } from "./sidebar/recent-chats";
import { RecentPolicies } from "./sidebar/recent-policies";
import { RecentProjectChats } from "./sidebar/recent-project-chats";

export default function Sidebar() {
  const user = useOptionalUser();
  const { t } = useTranslation(["nav", "common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const startTour = useStartTour();

  /**
   * Collapsed state lives on the shared context, not in here.
   *
   * `sidebarOpen` was provided by `UserUIContext` and read by nobody, so a
   * screen that needs the width — the policy split, which is a conversation and
   * a policy side by side — had no way to ask for it. Reading it here is what
   * makes the context mean what its name says; the rail's own toggle still
   * wins afterwards, because a screen only asks on the way in and out.
   */
  const { sidebarOpen, setSidebarOpen } = useContext(UserUIContext);
  const collapsed = !sidebarOpen;
  const setCollapsed = useCallback(
    (next: boolean | ((previous: boolean) => boolean)) => {
      setSidebarOpen(
        typeof next === "function" ? (open) => !next(!open) : !next,
      );
    },
    [setSidebarOpen],
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [initialTab, setInitialTab] = useState<
    "profile" | "security" | "preferences"
  >("profile");
  const [mfaRequiredNotice, setMfaRequiredNotice] = useState<boolean>(false);

  const settingsFetcher = useFetcher<typeof settingsDataLoader>();
  const planFetcher = useFetcher<typeof userPlanLoader>();
  const isSettingsLoadingRef = useRef(false);
  const isPlanLoadingRef = useRef(false);
  const wasAccountMenuOpenRef = useRef(false);
  const autoTourStartedRef = useRef(false);

  const resetChat = useChatStore((s) => s.resetChat);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const isDesignTimeExecuting = useChatStore((s) => s.isDesignTimeExecuting);
  const isWaitingForInput = useChatStore((s) => s.isWaitingForInput);
  const workflowRunStatus = useChatStore((s) => s.workflowRunStatus);
  const currentThinking = useChatStore((s) => s.currentThinking);
  const lastStreamEvent = useChatStore((s) => s.lastStreamEvent);
  const chatPlannedWorkflow = useChatStore((s) => s.plannedWorkflow);
  const currentSessionId = useChatStore((s) => s.currentSessionId);
  const chats = useSessionsStore((s) => s.chats);

  const isChatDesignRoute =
    location.pathname.startsWith("/u/alpha/agent") &&
    !location.pathname.includes("/workflow/");

  /** The "new chat" route — the sidebar's Chat item, and `links.tsx`'s `to`. */
  const CHAT_ROOT_PATH = "/u/alpha/agent";

  const sidebarRecentVariant = resolveSidebarRecentVariant(location.pathname);

  const renderRecentSidebar = (isCollapsed: boolean) => {
    if (sidebarRecentVariant === "workflow") {
      return (
        <RecentChats onNavigate={handleNavigate} collapsed={isCollapsed} />
      );
    }
    if (sidebarRecentVariant === "policy") {
      return (
        <RecentPolicies onNavigate={handleNavigate} collapsed={isCollapsed} />
      );
    }
    if (sidebarRecentVariant === "project") {
      return (
        <RecentProjectChats
          onNavigate={handleNavigate}
          collapsed={isCollapsed}
        />
      );
    }
    return null;
  };

  // Current session's workflowId (only set once the workflow is saved).
  const currentSessionWorkflowId = useMemo(() => {
    if (!currentSessionId) return null;
    return chats.find((c) => c.id === currentSessionId)?.workflowId ?? null;
  }, [currentSessionId, chats]);

  const blockingState = useMemo(
    () =>
      getDesignPhaseBlockingState({
        isStreaming,
        isDesignTimeExecuting,
        isWaitingForInput,
        workflowRunStatus,
        currentThinking,
        lastStreamEvent,
        plannedWorkflow: chatPlannedWorkflow,
        currentSessionWorkflowId:
          currentSessionWorkflowId != null
            ? String(currentSessionWorkflowId)
            : null,
      }),
    [
      chatPlannedWorkflow,
      isStreaming,
      isDesignTimeExecuting,
      isWaitingForInput,
      workflowRunStatus,
      currentThinking,
      lastStreamEvent,
      currentSessionWorkflowId,
    ],
  );

  // Only block if we're on a design route AND blocking state says to block.
  const shouldBlockLeavingDesign =
    isChatDesignRoute && blockingState.shouldBlock;

  const [leaveDesignOpen, setLeaveDesignOpen] = useState(false);
  const pendingNavigationRef = useRef<null | (() => void)>(null);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (!shouldBlockLeavingDesign) {
        action();
        return;
      }
      pendingNavigationRef.current = action;
      setLeaveDesignOpen(true);
    },
    [shouldBlockLeavingDesign],
  );

  const confirmLeaveDesign = useCallback(() => {
    setLeaveDesignOpen(false);
    const action = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    action?.();
  }, []);

  const cancelLeaveDesign = useCallback(() => {
    setLeaveDesignOpen(false);
    pendingNavigationRef.current = null;
  }, []);

  /** Every in-sidebar navigation goes through the design-phase guard. */
  const handleNavigate = useCallback(
    (to: string) => {
      // Close the mobile sheet on any sidebar destination, including the
      // current page (pathname may not change) and the leave-design confirm
      // path, so the drawer never sits over the destination or the dialog.
      setMobileMenuOpen(false);
      const go = () => {
        // "Chat" must always land on an empty thread, and it cannot rely on the
        // route change to do that. When a thread is created mid-session,
        // chat-main swaps the URL with a raw `window.history.pushState` (see the
        // `redirectToThreadId` effect there), so the browser shows
        // /u/alpha/agent/<id> while React Router still believes it is on the
        // index route. Navigating to /u/alpha/agent is then a no-op against an
        // unchanged location: no remount, no re-run of the effect that calls
        // `resetChat()`, and the finished thread stayed on screen under a
        // /u/alpha/agent URL. It only ever worked when you had reached the
        // thread through a real navigation, e.g. clicking it under Recent —
        // hence "sometimes".
        if (to === CHAT_ROOT_PATH) {
          resetChat();
          // Put the caret in the composer so the user can type immediately.
          // Retries until the textarea mounts after a cross-route navigation.
          focusChatComposer();
        }
        navigate(to);
      };
      if (!shouldBlockLeavingDesign) {
        go();
        return;
      }
      // Moving between chat design routes is not "leaving" the design phase.
      if (isChatDesignRoute && to.startsWith("/u/alpha/agent")) {
        go();
        return;
      }
      requestNavigation(go);
    },
    [
      shouldBlockLeavingDesign,
      isChatDesignRoute,
      navigate,
      requestNavigation,
      resetChat,
    ],
  );

  const handleOpenSettings = useCallback(
    (tab: "profile" | "security" | "preferences") => {
      setMobileMenuOpen(false);
      setInitialTab(tab);
      setSettingsOpen(true);
    },
    [],
  );

  const handleStartTour = useCallback(() => {
    setMobileMenuOpen(false);
    void startTour();
  }, [startTour]);

  // Open the settings modal when returning from Keycloak 2FA setup, or when
  // redirected here by the platform-admin MFA hard cut-over
  // (?settingsOpen=1&settingsTab=security&mfaRequired=1).
  // ?settingsTab= also deep-links Preferences / Profile.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("settingsOpen") === "1") {
      const requestedTab = params.get("settingsTab");
      setSettingsOpen(true);
      setInitialTab(
        requestedTab === "preferences" || requestedTab === "profile"
          ? requestedTab
          : "security",
      );
      if (params.get("mfaRequired") === "1") {
        setMfaRequiredNotice(true);
      }
      params.delete("settingsOpen");
      params.delete("settingsTab");
      params.delete("mfaRequired");
      params.delete("mfaIdpUnavailable");
      params.delete("mfaEnroll");
      const newSearch = params.toString();
      navigate(location.pathname + (newSearch ? `?${newSearch}` : ""), {
        replace: true,
      });
    }
  }, [location.pathname, location.search, navigate]);

  // There was a route-change effect here that tore stuck tour overlays out of the
  // DOM by selector. It existed because Shepherd appended its overlay to <body>
  // itself and left it there when a tour ended badly, which blocked every sidebar
  // click. react-joyride renders its overlay as part of the React tree, so it goes
  // when <TourMount> stops rendering it and cannot outlive the tour. The effect
  // also reset `globalThis.tourRunning`, which nothing in the app reads.

  // Load settings data when the modal opens.
  useEffect(() => {
    if (
      settingsOpen &&
      !settingsFetcher.data &&
      !isSettingsLoadingRef.current &&
      settingsFetcher.state === "idle"
    ) {
      isSettingsLoadingRef.current = true;
      settingsFetcher.load("/resources/settings-data");
    }
  }, [
    settingsOpen,
    settingsFetcher,
    settingsFetcher.data,
    settingsFetcher.state,
  ]);

  useEffect(() => {
    if (settingsFetcher.state === "idle") {
      isSettingsLoadingRef.current = false;
    }
  }, [settingsFetcher.state]);

  // The card shows the plan without the menu ever being opened, so waiting for
  // the open below left it on "Loading" for the whole session.
  useEffect(() => {
    if (
      !user ||
      planFetcher.data ||
      isPlanLoadingRef.current ||
      planFetcher.state !== "idle"
    ) {
      return;
    }
    isPlanLoadingRef.current = true;
    planFetcher.load("/resources/user-plan");
  }, [user, planFetcher, planFetcher.data, planFetcher.state]);

  // Refresh plan each time the account menu opens (edge-triggered) so Free/Pro
  // stays in sync with billing instead of keeping a stale session cache.
  useEffect(() => {
    const justOpened = accountMenuOpen && !wasAccountMenuOpenRef.current;
    wasAccountMenuOpenRef.current = accountMenuOpen;
    if (!justOpened || !user) return;
    isPlanLoadingRef.current = true;
    planFetcher.load("/resources/user-plan");
  }, [accountMenuOpen, user, planFetcher]);

  // Preload settings data when the account menu opens so SettingsModal opens quickly.
  useEffect(() => {
    if (
      accountMenuOpen &&
      user &&
      !settingsFetcher.data &&
      !isSettingsLoadingRef.current &&
      settingsFetcher.state === "idle"
    ) {
      isSettingsLoadingRef.current = true;
      settingsFetcher.load("/resources/settings-data");
    }
  }, [
    accountMenuOpen,
    user,
    settingsFetcher,
    settingsFetcher.data,
    settingsFetcher.state,
  ]);

  useEffect(() => {
    if (planFetcher.state === "idle") {
      isPlanLoadingRef.current = false;
    }
  }, [planFetcher.state]);

  useEffect(() => {
    if (!user || typeof window === "undefined") return;
    const handleOrgTeamSelectionChanged = () => {
      isPlanLoadingRef.current = true;
      planFetcher.load("/resources/user-plan");
    };
    window.addEventListener(
      ORG_TEAM_SELECTION_CHANGED_EVENT,
      handleOrgTeamSelectionChanged,
    );
    return () =>
      window.removeEventListener(
        ORG_TEAM_SELECTION_CHANGED_EVENT,
        handleOrgTeamSelectionChanged,
      );
  }, [planFetcher, user]);

  useEffect(() => {
    if (settingsFetcher.data && "error" in settingsFetcher.data) {
      console.error(
        "Failed to load settings data:",
        settingsFetcher.data.error,
      );
    }
  }, [settingsFetcher, settingsFetcher.data]);

  // Auto-start the tour when redirected from onboarding.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.get("startTour") === "build-first-agent" &&
      !autoTourStartedRef.current
    ) {
      autoTourStartedRef.current = true;

      // Clean the URL first so a refresh does not re-run the tour.
      params.delete("startTour");
      const search = params.toString();
      navigate(`${location.pathname}${search ? `?${search}` : ""}`, {
        replace: true,
      });

      setTimeout(() => void startTour(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const { i18n } = useTranslation();
  const plan = useMemo<ProfilePlan>(() => {
    const planData =
      planFetcher.data && "userPlan" in planFetcher.data
        ? planFetcher.data.userPlan
        : null;
    if (!planData) return null;

    const planType = (planData.type ?? "free").toLowerCase();
    const rawPlanName = planData.plan?.planName ?? "Free";
    const dateLocale = i18n.language?.startsWith("ja") ? "ja-JP" : "en-US";

    return {
      name: t(`nav:accountMenu.planNames.${rawPlanName.toLowerCase()}`, {
        defaultValue: rawPlanName,
      }),
      typeLabel: t(`nav:accountMenu.planTypes.${planType}`, {
        defaultValue: planType,
      }),
      isFree: planType === "free",
      isEnterprise: planType === "enterprise",
      renewsOn: planData.expiryAt
        ? new Date(planData.expiryAt).toLocaleDateString(dateLocale, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : null,
    };
  }, [planFetcher.data, t, i18n.language]);

  // Loading until the first fetch resolves, so the first paint shows a loader
  // instead of flashing "Free Plan".
  const isPlanLoading = Boolean(user) && !planFetcher.data;

  return (
    <>
      {/* `.sidebar` is load-bearing: HamburgerMenu toggles `.open` on it for the
          mobile drawer, and translates `.layout-right-block` to match. */}

      {/* <aside
        className={cn(
          "sidebar fixed inset-y-0 left-0 z-50 flex h-dvh shrink-0 -translate-x-full flex-col overflow-hidden border-r border-zinc-100 bg-white p-4 pt-2.5 transition-[width,transform] duration-300 ease-in-out sm:sticky sm:top-0 sm:translate-x-0",
          collapsed ? "w-[3.625rem]" : "w-70",
        )}
      >
        <MainLogo
          collapsed={collapsed}
          onToggle={() => setCollapsed((prev) => !prev)}
          onNavigate={handleNavigate}
        />

        <MenuItems
          collapsed={collapsed}
          pathname={location.pathname}
          onNavigate={handleNavigate}
        />

        {collapsed ? <Separator className="my-3" /> : null}

        {renderRecentSidebar(collapsed)}

        {user ? (
          <ProfileDropdown
            collapsed={collapsed}
            user={user}
            plan={plan}
            isPlanLoading={isPlanLoading}
            onOpenChange={setAccountMenuOpen}
            onOpenSettings={handleOpenSettings}
            onNavigate={handleNavigate}
            onStartTour={handleStartTour}
          />
        ) : null}
      </aside> */}

      {/* Mobile hamburger + tablet icon rail share one full navigation sheet. */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <div className="fixed left-2 top-2.5 z-[50] md:hidden">
          {/* `render`, not a nested child. `SheetTrigger` renders its own
              <button>, so wrapping a <Button> inside it produced
              <button><button>, which is invalid HTML -- React refuses to
              hydrate it and regenerates the whole tree on the client. `render`
              merges the trigger INTO the Button instead, which is how every
              other trigger in this codebase is written
              (e.g. `organization-list.tsx:443`). */}
          <SheetTrigger
            aria-label="Open menu"
            render={<Button variant="secondary" size="icon" />}
          >
            <IconMenu size={28} strokeWidth={3} />
          </SheetTrigger>
        </div>

        {/* Tablet navigation: a persistent icon rail reserves its own width.
            The expand control opens the full navigation as an overlay so page
            content never shifts between the collapsed and expanded states. */}
        <aside className="sidebar sticky top-0 hidden h-dvh w-[3.625rem] shrink-0 flex-col overflow-hidden border-r border-zinc-100 bg-[#FAFCFD] p-4 pt-2.5 md:flex xl:hidden dark:border-border dark:bg-zinc-900">
          <MainLogo
            collapsed
            onToggle={() => setMobileMenuOpen(true)}
            onNavigate={handleNavigate}
          />

          <MenuItems
            collapsed
            pathname={location.pathname}
            onNavigate={handleNavigate}
          />

          <Separator className="my-3" />

          {renderRecentSidebar(true)}

          {user ? (
            <ProfileDropdown
              collapsed
              user={user}
              plan={plan}
              isPlanLoading={isPlanLoading}
              onOpenChange={setAccountMenuOpen}
              onOpenSettings={handleOpenSettings}
              onNavigate={handleNavigate}
              onStartTour={handleStartTour}
            />
          ) : null}
        </aside>

        <SheetContent
          side="left"
          showCloseButton={false}
          className="w-[280px] gap-0 px-4 pt-2.5 sm:max-w-none dark:border-border dark:bg-sidebar"
        >
          <div className="flex h-full flex-col">
            <MainLogo
              collapsed={false}
              onToggle={() => setMobileMenuOpen(false)}
              onNavigate={handleNavigate}
            />

            <MenuItems
              collapsed={false}
              pathname={location.pathname}
              onNavigate={handleNavigate}
            />

            {renderRecentSidebar(false)}

            {user ? (
              <ProfileDropdown
                collapsed={false}
                user={user}
                plan={plan}
                isPlanLoading={isPlanLoading}
                onOpenChange={setAccountMenuOpen}
                onOpenSettings={handleOpenSettings}
                onNavigate={handleNavigate}
                onStartTour={handleStartTour}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          "sidebar sticky top-0 hidden h-dvh shrink-0 flex-col overflow-hidden border-r border-zinc-100 p-4 pt-2.5 transition-[width] duration-300 ease-in-out xl:flex dark:border-border",
          collapsed
            ? "w-[3.625rem] bg-[#FAFCFD] dark:bg-zinc-900"
            : "w-70 bg-white dark:bg-sidebar",
        )}
      >
        <MainLogo
          collapsed={collapsed}
          onToggle={() => setCollapsed((prev) => !prev)}
          onNavigate={handleNavigate}
        />

        <MenuItems
          collapsed={collapsed}
          pathname={location.pathname}
          onNavigate={handleNavigate}
        />

        {collapsed ? <Separator className="my-3" /> : null}

        {renderRecentSidebar(collapsed)}

        {user ? (
          <ProfileDropdown
            collapsed={collapsed}
            user={user}
            plan={plan}
            isPlanLoading={isPlanLoading}
            onOpenChange={setAccountMenuOpen}
            onOpenSettings={handleOpenSettings}
            onNavigate={handleNavigate}
            onStartTour={handleStartTour}
          />
        ) : null}
      </aside>

      <ConfirmDialog
        open={leaveDesignOpen}
        onOpenChange={(open) => {
          if (!open) cancelLeaveDesign();
        }}
        title={blockingState.title || t("common:leaveDesignPhase")}
        description={
          <div className="space-y-2">
            <p>{blockingState.message || t("common:leaveDesignMessage")}</p>
            {!blockingState.message ? (
              <p className="text-xs text-[#939AA8]">
                {t("common:stayInDesignHint")}
              </p>
            ) : null}
          </div>
        }
        confirmText={t("common:leaveAnyway")}
        cancelText={t("common:stay")}
        onConfirm={confirmLeaveDesign}
        onCancel={cancelLeaveDesign}
      />

      {user && settingsOpen
        ? (() => {
            const settingsData =
              settingsFetcher.data && "user" in settingsFetcher.data
                ? settingsFetcher.data
                : null;

            const fallbackUser = {
              id: user.id,
              name: user.name,
              username: user.username,
              email: user.email || user.username,
              image: user.image,
              _count: { sessions: 0 },
            };

            return (
              <SettingsModal
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                user={settingsData?.user || fallbackUser}
                hasPassword={settingsData?.hasPassword ?? false}
                isTwoFactorEnabled={settingsData?.isTwoFactorEnabled ?? false}
                isTwoFactorSetupPending={
                  settingsData?.isTwoFactorSetupPending ?? false
                }
                keycloakId={settingsData?.keycloakId ?? null}
                keycloakAccountUrl={settingsData?.keycloakAccountUrl ?? null}
                hasConfiguredMfa={settingsData?.hasConfiguredMfa ?? false}
                mfaIdpUnavailable={settingsData?.mfaIdpUnavailable ?? false}
                isPlatformAdmin={settingsData?.isPlatformAdmin ?? false}
                canDisableTwoFactor={settingsData?.canDisableTwoFactor ?? true}
                mfaRequiredNotice={mfaRequiredNotice}
                initialTab={initialTab}
                onUpdate={() => {
                  if (settingsFetcher.state === "idle") {
                    isSettingsLoadingRef.current = false;
                    settingsFetcher.load("/resources/settings-data");
                  }
                }}
              />
            );
          })()
        : null}
    </>
  );
}
